import styled, { createGlobalStyle, keyframes } from 'styled-components'

const slideUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`

const c = {
  bg: '#0a0c0f', bg2: '#111318', bg3: '#181c23', bg4: '#1e2330',
  bdr: '#1f2430', bdr2: '#2a3040',
  acc: '#00e5a0', acc2: '#00b87a',
  adim: 'rgba(0,229,160,.08)', adim2: 'rgba(0,229,160,.18)',
  txt: '#e8ecf0', txt2: '#8a95a3', txt3: '#5a6370',
  warn: '#f59e0b', danger: '#ef4444', info: '#3b82f6',
  mono: "'Space Mono',monospace", sans: "'DM Sans',sans-serif",
}

export const AREA_PALETTE = [
  '#00e5a0', '#3b82f6', '#f59e0b', '#a855f7', '#06b6d4',
  '#ec4899', '#10b981', '#f97316', '#6366f1', '#84cc16',
  '#fb923c', '#14b8a6', '#8b5cf6', '#ef4444', '#0ea5e9',
]

export const GlobalAreasStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

  .leaflet-popup-content-wrapper {
    background: ${c.bg2} !important; border: 1px solid ${c.bdr2} !important;
    border-radius: 10px !important; box-shadow: 0 8px 32px rgba(0,0,0,.6) !important;
    padding: 0 !important;
  }
  .leaflet-popup-tip { background: ${c.bg2} !important; }
  .leaflet-popup-content { margin: 0 !important; width: 220px !important; }
  .leaflet-tooltip {
    background: ${c.bg2} !important; border: 1px solid ${c.bdr2} !important;
    color: ${c.txt} !important; font-size: 10px !important;
    border-radius: 6px !important; padding: 4px 8px !important;
    box-shadow: 0 4px 16px rgba(0,0,0,.5) !important;
  }
  .area-popup .pu { padding: 12px 14px; }
  .area-popup .pu-name { font-family: 'Space Mono',monospace; font-size: 12px; font-weight: 700; margin-bottom: 8px; }
  .area-popup .pu-row { display:flex; justify-content:space-between; font-size:10px; padding:3px 0; border-bottom:1px solid ${c.bdr}; }
  .area-popup .pu-row:last-of-type { border:none; }
  .area-popup .pu-row .k { color:${c.txt3}; }
  .area-popup .pu-row .v { color:${c.txt}; font-family: 'Space Mono',monospace; }

  .area-vertex-handle {
    width: 12px; height: 12px; border-radius: 50%;
    background: #fff; border: 2px solid #000;
    box-shadow: 0 0 0 2px rgba(255,255,255,.6); cursor: move;
  }
  .area-vertex-ghost {
    width: 12px; height: 12px; border-radius: 50%;
    background: rgba(255,255,255,.15);
    border: 2px dashed ${c.acc};
    cursor: copy; transition: .15s;
  }
  .area-vertex-ghost:hover {
    background: ${c.acc}; border-style: solid; transform: scale(1.25);
  }
`

export const PageWrap = styled.div`
  background:${c.bg}; color:${c.txt}; font-family:${c.sans};
  height:100vh; display:flex; flex-direction:column; overflow:hidden;
`

export const Topbar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:10px 16px; height:48px;
  display:flex; align-items:center; gap:14px; flex-shrink:0;
  h2 { font-size:13px; font-weight:600; color:${c.txt}; }
  .crumb { font-size:11px; color:${c.txt3}; }
`

export const Body = styled.div`flex:1; display:flex; min-height:0;`

export const Sidebar = styled.div`
  width:320px; flex-shrink:0;
  background:${c.bg2}; border-right:1px solid ${c.bdr};
  display:flex; flex-direction:column; overflow:hidden;
`

export const SideHeader = styled.div`
  padding:12px; border-bottom:1px solid ${c.bdr}; flex-shrink:0;
  h3 { font-size:12px; font-weight:600; color:${c.txt}; margin-bottom:4px; }
  p { font-size:11px; color:${c.txt3}; line-height:1.5; }
`

export const DrawBtn = styled.button`
  width:100%; padding:9px;
  background:${({ $active }) => $active ? c.acc : c.adim2};
  border:1px solid ${({ $active }) => $active ? c.acc : c.acc2};
  border-radius:8px; color:${({ $active }) => $active ? '#000' : c.acc};
  font-size:12px; font-weight:600; cursor:pointer; transition:.15s; font-family:${c.sans};
  margin-top:8px;
  &:hover { background:${c.acc}; color:#000; }
`

export const AreaList = styled.div`
  overflow-y:auto; flex:1; padding:9px;
  &::-webkit-scrollbar { width:3px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
`

export const NoAreas = styled.div`
  text-align:center; padding:40px 20px;
  color:${c.txt3}; font-size:12px; line-height:1.6;
`

export const ACard = styled.div`
  /* Em modo edição o box assume a cor da área (com overlay escuro pra manter legibilidade) */
  background: ${({ $editing, $color }) =>
    $editing && $color
      ? `linear-gradient(rgba(10,12,15,.62), rgba(10,12,15,.62)), ${$color}`
      : c.bg3};
  border:1px solid ${({ $editing, $color }) =>
    $editing && $color ? `${$color}` : c.bdr2};
  border-radius:9px; margin-bottom:8px; overflow:hidden;
  border-left:3px solid ${({ $color }) => $color || c.txt3};
  transition: background .2s ease, border-color .2s ease;
  /* Reforço visual nos textos quando em edição */
  ${({ $editing }) => $editing && `
    box-shadow: 0 4px 18px rgba(0,0,0,.4);
  `}
`

export const ACardHead = styled.div`
  display:flex; align-items:center; gap:8px; padding:9px 10px;
`

export const ASwatch = styled.div`
  width:10px; height:10px; border-radius:2px;
  background:${({ $color }) => $color}; flex-shrink:0;
`

export const AName = styled.div`
  flex:1; min-width:0; font-size:12px; font-weight:600; color:${c.txt};
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:pointer;
  &:hover { color:${c.acc}; }
`

export const ABtn = styled.button`
  font-size:11px; color:${c.txt3}; background:transparent; border:none;
  cursor:pointer; padding:3px 7px; border-radius:4px; line-height:1;
  &:hover { background:${c.bg4}; color:${({ $danger, $warn }) => $danger ? c.danger : ($warn ? c.warn : c.txt)}; }
  &:disabled { opacity:.4; cursor:not-allowed; pointer-events:none; }
`

export const AStats = styled.div`
  display:flex; gap:5px; padding:0 10px 9px; flex-wrap:wrap;
`

export const AStat = styled.div`
  background:${c.bg4}; border-radius:5px; padding:4px 8px;
  font-size:10px; color:${c.txt2}; font-family:${c.mono};
  span { color:${c.txt}; font-weight:700; }
`

export const Footer = styled.div`
  padding:8px 12px; border-top:1px solid ${c.bdr};
  font-size:11px; color:${c.txt3}; display:flex; justify-content:space-between;
  span:last-child { font-family:${c.mono}; }
  flex-shrink:0;
`

export const MapContainer = styled.div`flex:1; position:relative; height:100%; min-width:0;`
export const MapDiv = styled.div`position:absolute; top:0; right:0; bottom:0; left:0;`

export const PolyHint = styled.div`
  display:${({ $visible }) => $visible ? 'block' : 'none'};
  position:absolute; top:14px; left:50%; transform:translateX(-50%);
  z-index:600; background:${c.bg2}; border:1px solid ${c.acc2}; border-radius:8px;
  padding:7px 16px; font-size:11px; color:${c.acc};
  pointer-events:none; white-space:nowrap; box-shadow:0 4px 16px rgba(0,0,0,.5);
  animation:${slideUp} .2s ease;
`

export const MapCtrl = styled.div`
  position:absolute; top:10px; right:10px; z-index:500;
  display:flex; flex-direction:column; gap:5px;
`

export const CtrlBtn = styled.button`
  background:${c.bg2}; border:1px solid ${({ $active }) => $active ? c.warn : c.bdr2};
  border-radius:7px; color:${({ $active }) => $active ? c.warn : c.txt2};
  font-size:11px; padding:6px 11px; cursor:pointer; transition:.15s; white-space:nowrap;
  &:hover { border-color:${c.acc}; color:${c.acc}; }
`

export const MapStatus = styled.div`
  position:absolute; bottom:16px; right:10px; z-index:500;
  background:${c.bg2}; border:1px solid ${c.bdr}; border-radius:7px;
  padding:6px 11px; font-size:10px; color:${c.txt3}; font-family:${c.mono};
`

export const ModalOverlay = styled.div`
  position:fixed; inset:0; z-index:2000;
  background:rgba(0,0,0,.6); backdrop-filter:blur(2px);
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; justify-content:center;
`
export const ModalBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:12px;
  padding:20px; min-width:340px; max-width:420px;
  box-shadow:0 16px 48px rgba(0,0,0,.7);
  h3 { font-size:14px; font-weight:600; margin-bottom:6px; color:${c.txt}; }
  p { font-size:12px; color:${c.txt3}; margin-bottom:14px; line-height:1.5; }
`
export const ModalInput = styled.input`
  width:100%; background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:8px;
  color:${c.txt}; padding:9px 12px; font-size:13px; font-family:${c.sans}; outline:none;
  &:focus { border-color:${c.acc}; }
`
export const ModalSelect = styled.select`
  width:100%; background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:8px;
  color:${c.txt}; padding:9px 12px; font-size:13px; font-family:${c.sans}; outline:none;
  cursor:pointer; appearance:none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%238a95a3' stroke-width='1.5'%3E%3Cpath d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 12px center; background-size:12px;
  padding-right:32px;
  &:focus { border-color:${c.acc}; }
  &:disabled { opacity:.5; cursor:not-allowed; }
  option { background:${c.bg3}; color:${c.txt}; }
`
export const ModalActions = styled.div`
  display:flex; justify-content:flex-end; gap:8px; margin-top:16px;
`
export const ModalBtn = styled.button`
  padding:7px 16px; border-radius:7px; cursor:pointer;
  font-size:12px; font-weight:600; transition:.15s;
  background:${({ $primary }) => $primary ? c.acc : 'transparent'};
  border:1px solid ${({ $primary }) => $primary ? c.acc : c.bdr2};
  color:${({ $primary }) => $primary ? '#000' : c.txt2};
  &:hover { ${({ $primary }) => $primary ? 'filter:brightness(1.1);' : `border-color:${c.acc}; color:${c.acc};`} }
  &:disabled { opacity:.5; cursor:not-allowed; }
`

export const ModalLabel = styled.div`
  font-size:11px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.05em;
  margin:14px 0 6px;
`
export const ColorPickerWrap = styled.div`
  display:grid; grid-template-columns:repeat(8, 1fr); gap:6px;
`
export const ColorSwatch = styled.button`
  width:100%; aspect-ratio:1; border-radius:6px; cursor:pointer;
  background:${({ $color }) => $color}; padding:0;
  border:2px solid ${({ $selected }) => $selected ? '#fff' : 'transparent'};
  box-shadow:${({ $selected }) => $selected ? '0 0 0 2px rgba(0,229,160,.6)' : 'none'};
  transition:.12s;
  &:hover { transform:scale(1.1); }
`
export const CustomColorRow = styled.div`
  display:flex; align-items:center; gap:10px; margin-top:10px;
  font-size:11px; color:${c.txt2};
  input[type="color"] {
    width:42px; height:30px; padding:0; border:1px solid ${c.bdr2};
    border-radius:6px; background:${c.bg3}; cursor:pointer;
  }
  .hex {
    font-family:${c.mono}; font-size:11px; color:${c.txt}; padding:5px 10px;
    background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:6px;
  }
`

// ─── CONFIRM MODAL ───────────────────────────────────────────────────────────
export const ConfirmBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:12px;
  padding:22px 24px; min-width:340px; max-width:440px;
  box-shadow:0 16px 48px rgba(0,0,0,.7);
  border-top:3px solid ${({ $tone }) => $tone === 'danger' ? c.danger : c.warn};
`
export const ConfirmIcon = styled.div`
  width:42px; height:42px; border-radius:50%;
  background:${({ $tone }) => $tone === 'danger' ? 'rgba(239,68,68,.15)' : 'rgba(245,158,11,.15)'};
  color:${({ $tone }) => $tone === 'danger' ? c.danger : c.warn};
  display:flex; align-items:center; justify-content:center;
  font-size:22px; margin-bottom:12px;
`
export const ConfirmTitle = styled.h3`
  font-size:15px; font-weight:600; color:${c.txt}; margin-bottom:6px;
`
export const ConfirmMessage = styled.p`
  font-size:12px; color:${c.txt2}; line-height:1.6; margin-bottom:18px;
  white-space:pre-line; max-height:50vh; overflow-y:auto;
`
export const ConfirmActions = styled.div`
  display:flex; justify-content:flex-end; gap:8px;
`
export const ConfirmBtn = styled.button`
  padding:8px 18px; border-radius:7px; cursor:pointer;
  font-size:12px; font-weight:600; transition:.15s; font-family:${c.sans};
  background:${({ $primary, $tone }) =>
    $primary ? ($tone === 'danger' ? c.danger : c.warn) : 'transparent'};
  border:1px solid ${({ $primary, $tone }) =>
    $primary ? ($tone === 'danger' ? c.danger : c.warn) : c.bdr2};
  color:${({ $primary }) => $primary ? '#fff' : c.txt2};
  &:hover { ${({ $primary }) => $primary ? 'filter:brightness(1.1);' : `border-color:${c.acc}; color:${c.acc};`} }
`